import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { shareJobCard } from '../../../lib/jobCardShare';
import { formatErr } from '../billing/shared';

const ago = (n) => (n <= 1 ? 'Delivered yesterday' : `Delivered ${n} days ago`);

// Cars delivered yesterday (up to 3 days back): one tap sends the thank-you card + review link.
export const ReviewFollowups = ({ reviewUrl }) => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    axios.get(`${API}/job-cards/review-followups/due`, { headers: authHeader() })
      .then((r) => setRows(r.data || [])).catch((e) => { setRows([]); toast.error(formatErr(e)); });
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const send = async (card) => {
    shareJobCard(card, authHeader, 'thanks', reviewUrl);
    setBusy(card.id);
    try {
      await axios.post(`${API}/job-cards/${card.id}/review-followup-sent`, {}, { headers: authHeader() });
      setRows((rs) => rs.filter((r) => r.id !== card.id));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(''); }
  };

  if (rows === null) return null;
  return (
    <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50/60 p-4" data-testid="review-followups">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PartyPopper size={16} className="text-teal-700" />
        <h2 className="text-sm font-semibold text-slate-900">Review follow-ups due <span className="text-slate-500" data-testid="review-followups-count">({rows.length})</span></h2>
        <span className="text-xs text-slate-500">A thank-you card with a QR to your Google review page, the day after delivery.</span>
      </div>
      {!reviewUrl && <p className="mb-2 text-xs text-amber-800" data-testid="review-followups-no-link">Add your Google review link in Billing Settings so the card shows a QR code.</p>}
      {rows.length === 0 && <p className="text-xs text-slate-500" data-testid="review-followups-empty">Nothing due. Cars you mark as delivered show up here the next day.</p>}
      <div className="divide-y divide-teal-100">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 py-2" data-testid={`review-followup-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-800">{r.customer_snapshot?.name} <span className="font-mono text-[11px] font-normal text-slate-500">{r.customer_snapshot?.phone}</span></div>
              <div className="truncate text-xs text-slate-500">{[r.number, [r.vehicle_snapshot?.make, r.vehicle_snapshot?.model, r.vehicle_snapshot?.registration_number].filter(Boolean).join(' '), ago(r.days_ago)].filter(Boolean).join(' · ')}</div>
            </div>
            <button type="button" onClick={() => send(r)} disabled={busy === r.id} className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50" data-testid={`review-followup-send-${r.id}`}>
              {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />} Send thank-you
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
